import React, { useState } from 'react';
import { useFormikContext } from 'formik';

import { useClaim } from '~/components/ClaimContainer';
import { ConfirmModal } from '~/components/ConfirmModal';
import MenuItem from '~/components/core/Atomic/MenuItem';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import Text from '~/components/core/TextComponents/Text';
import useCanSendAsOtherAdjuster from '~/components/hooks/useCanSendAsOtherAdjuster';
import { useCms } from '~/components/hooks/useCms';
import { useAvailableEmailSenders } from '~/components/SystemConfiguration/Users/hooks/useAvailableEmailSenders';

interface FormikValues {
  send_as_user_id?: string | null;
}

interface SupervisorSenderSelectionProps {
  onChange: (userId: string | null) => void;
}

const SupervisorSenderSelection: React.FC<SupervisorSenderSelectionProps> = ({ onChange }) => {
  const { values } = useFormikContext<FormikValues>();
  const { claim } = useClaim();
  const { user } = useCms();
  const canSendAsOtherAdjuster = useCanSendAsOtherAdjuster();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    users: availableSenders,
    isLoading,
    error,
  } = useAvailableEmailSenders({
    organizationId: user?.organization_id,
    enabled: canSendAsOtherAdjuster,
    subOrgId: claim?.policy?.sub_organization_id,
  });

  // Only show for supervisors/unit leaders
  if (!canSendAsOtherAdjuster) {
    return null;
  }

  const handleChange = (newUserId: string | null) => {
    // If it's the same user, no need for confirmation
    if (newUserId === values.send_as_user_id) {
      return;
    }

    // Show confirmation modal
    setPendingUserId(newUserId);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmChange = () => {
    setIsProcessing(true);

    // Always call the parent onChange handler
    onChange(pendingUserId);

    // Close modal and reset state
    setIsConfirmModalOpen(false);
    setPendingUserId(null);
    setIsProcessing(false);
  };

  const handleCancelChange = () => {
    setIsConfirmModalOpen(false);
    setPendingUserId(null);
  };

  const getPendingUserName = () => {
    if (!pendingUserId) return 'selected user';
    if (pendingUserId === user.id) return 'yourself';
    const pendingUser = availableSenders.find((u) => u.id === pendingUserId);
    return pendingUser ? pendingUser.username : 'selected user';
  };

  return (
    <>
      <div className="mt-6 mb-6">
        <SingleSelectField
          id="send_as_user_id"
          label="Select User to Send As"
          value={values.send_as_user_id || ''}
          onChange={handleChange}
          disabled={isLoading}
          // min width set to accommodate label text in one line regardless of contact name length
          className="w-1/2 min-w-[150px] max-w-[280px]"
        >
          <MenuItem key="self" value={user.id}>
            Send as myself ({user.username})
          </MenuItem>
          {availableSenders
            .filter((auser) => auser.id !== user.id)
            ?.map((sender) => (
              <MenuItem key={sender.id} value={sender.id}>
                {sender.username}
              </MenuItem>
            ))}
        </SingleSelectField>
        {values.send_as_user_id && values.send_as_user_id !== user.id && (
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.DISABLED} className="mt-1 opacity-70">
            Email will be sent as the selected user, but you will be recorded as the actual sender.
          </Text>
        )}
        {error && (
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.ERROR} className="mt-1">
            Error loading available email senders: {error}
          </Text>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        isSubmitting={isProcessing}
        title="Change Email Sender"
        contentText={`Changing the sender will replace the entire email body with the new user's signature. Any existing content will be lost. Are you sure you want to continue and send as ${getPendingUserName()}?`}
        primaryButtonName="Yes, Change Sender"
        secondaryButtonName="Cancel"
        onClose={handleCancelChange}
        onPrimaryBtnClick={handleConfirmChange}
      />
    </>
  );
};

export default SupervisorSenderSelection;
